import { Button } from "@mui/material";
import { FC, useCallback, useState } from "react";
import { exportCallsCallable } from "../../Calls/callables/exportCallsCallable";
import { useT } from "../../Translation/hooks/useT";
import { ProjectController } from "../hooks/useProjectController";

interface Props {
  projectController: ProjectController;
  projectId: string;
  onError?: (error: string | null) => void;
}

export const ExportCsvButton: FC<Props> = (props) => {
  const { projectController, projectId, onError } = props;
  const t = useT();
  const [isExporting, setIsExporting] = useState(false);

  const validateDateRange = useCallback(() => {
    const fromDate = projectController.fromDate;
    const toDate = projectController.toDate;

    const diffInDays = toDate.diff(fromDate, "days").days;

    if (diffInDays > 90) {
      const error = t(
        "ProjectControllerView_Error_ExportDateRangeExceeds90Days",
      );
      onError?.(error);
      return false;
    }

    onError?.(null);
    return true;
  }, [projectController.fromDate, projectController.toDate, onError, t]);

  const downloadCsv = useCallback((csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, []);

  const handleExport = useCallback(async () => {
    if (!validateDateRange()) {
      return;
    }

    setIsExporting(true);

    try {
      const result = await exportCallsCallable({
        projectId,
        startDate: projectController.fromDate.toISODate(),
        endDate: projectController.toDate.toISODate(),
      });

      downloadCsv(result.csvContent, result.filename);
    } catch (error) {
      console.error("Export failed:", error);
      const errorMsg = error instanceof Error ? error.message : "Export failed";
      onError?.(errorMsg);
    } finally {
      setIsExporting(false);
    }
  }, [
    validateDateRange,
    projectId,
    projectController.fromDate,
    projectController.toDate,
    downloadCsv,
    onError,
  ]);

  return (
    <Button
      size="small"
      variant="outlined"
      onClick={handleExport}
      disabled={isExporting}
    >
      {t("ProjectControllerView_Button_ExportCsv")}
    </Button>
  );
};
